'use client'

import { useTranslate } from '@repo/shared/hooks'
import { useUserAuthDetails } from '@repo/services'
import {
  Button,
  Card,
  Col,
  Modal,
  ModalContent,
  ModalTrigger,
  Row,
  Separator,
  Typography,
} from '@repo/ui/components'
import { useIsDesktop } from '@repo/ui/hooks'
import { AccountCTA } from './account.cta'
import { ChangeEmailKycContainer } from './change-email-kyc'
import { ChangeMobileKycContainer } from './change-mobile-kyc'
import { KycAlert, KycBlocker } from '@repo/shared/components'

export const AccountContainer = () => {
  const { t } = useTranslate()
  const { data: authDetails, isPending } = useUserAuthDetails()
  const hasKyc =
    authDetails?.data.details?.kyc &&
    authDetails?.data.details?.kyc?.status === 'approved'

  const showKycAlert = !(hasKyc || isPending) && authDetails?.data

  const personalInfo = [
    {
      label: t('profilePage_firstName'),
      value: authDetails?.data?.details?.firstName,
    },
    {
      label: t('profilePage_lastName'),
      value: authDetails?.data?.details?.lastName,
    },
    {
      label: t('profilePage_birthDate'),
      value: authDetails?.data?.details?.birthDate,
    },
    {
      label: t('profilePage_nationalId'),
      value: authDetails?.data?.nationalID,
    },
  ]

  const contactInfo = [
    {
      label: t('profilePage_phoneNumber'),
      value: authDetails?.data?.phone?.replace('+98', '0'),
      valueClass: 'ltr',
      action: (
        <Modal>
          <ModalTrigger asChild>
            <Button variant='ghost' size='sm' color='base'>
              {t(
                authDetails?.data?.phone && authDetails?.data?.phone !== ''
                  ? 'profilePage_editPhoneNumber'
                  : 'profilePage_registerPhoneNumber',
              )}
            </Button>
          </ModalTrigger>
          <ModalContent className='sm:max-w-[425px]'>
            {hasKyc ? (
              <ChangeMobileKycContainer
                hasValue={authDetails?.data?.phone !== ''}
              />
            ) : (
              <KycBlocker
                phone={authDetails?.data.phone}
                email={authDetails?.data.email}
                kyc={authDetails?.data.details?.kyc}
              />
            )}
          </ModalContent>
        </Modal>
      ),
    },
    {
      label: t('profilePage_email'),
      value: authDetails?.data?.email,
      action: (
        <Modal>
          <ModalTrigger asChild>
            <Button variant='ghost' size='sm' color='base'>
              {t(
                authDetails?.data?.email && authDetails?.data?.email !== ''
                  ? 'profilePage_editEmail'
                  : 'profilePage_registerEmail',
              )}
            </Button>
          </ModalTrigger>
          <ModalContent className='sm:max-w-[425px]'>
            {hasKyc ? (
              <ChangeEmailKycContainer
                hasValue={authDetails?.data?.email !== ''}
              />
            ) : (
              <KycBlocker
                phone={authDetails?.data.phone}
                email={authDetails?.data.email}
                kyc={authDetails?.data.details?.kyc}
              />
            )}
          </ModalContent>
        </Modal>
      ),
    },
  ]

  const isDesktop = useIsDesktop()

  return (
    <Card className='!gap-0' withoutBorder>
      {isDesktop && <AccountCTA origin='container' />}

      {showKycAlert && <KycAlert className='my-xl' data={authDetails?.data} />}

      <Col className='pt-3xl'>
        {personalInfo?.map((item, index) => (
          <Row key={index} className='justify-between py-xs'>
            <Typography
              size='sm'
              weight='regular'
              color='text-neutral-surface-80'
            >
              {item.label}
            </Typography>
            <Typography size='sm' weight='bold'>
              {item.value}
            </Typography>
          </Row>
        ))}
      </Col>

      <Separator orientation='horizontal' className='my-3xl' />

      {contactInfo?.map((item, index) => (
        <Row key={index} className='justify-between py-xs'>
          <Typography
            size='sm'
            weight='regular'
            color='text-neutral-surface-80'
          >
            {item.label}
          </Typography>
          <Row className='gap-2 w-auto'>
            <Typography
              size={!item.value ? 'xs' : 'sm'}
              weight={!item.value ? 'regular' : 'bold'}
              color={
                !item.value
                  ? 'text-neutral-surface-60'
                  : 'text-neutral-surface-100'
              }
              className={item.valueClass}
            >
              {item.value || t('profilePage_unregistered')}
            </Typography>
            {item.action}
          </Row>
        </Row>
      ))}
    </Card>
  )
}
